import * as React from "react";

import { AsyncComponent2 } from "@impero-com/common/components/asyncComponent";
import {
    DataTable,
    DATA_TABLE_MODE_VIEWPORT,
    FormattedView,
} from "@impero-com/common/components/dataTable";
import { TableFilter } from "@impero-com/common/components/filter";
import { NoAccessCard } from "@impero-com/common/components/noAccessCard/index";
import { ProtectedLink } from "@impero-com/common/components/protectedLink";
import { LoggedInHTTPError } from "@impero-com/common/errorPages/loggedInHTTPError";
import { getErrorPageTitle } from "@impero-com/common/errorPages/sharedError";
import createIcon from "@impero-com/common/img/create-icon.svg";
import { AjaxManager } from "@impero-com/common/utils/ajax";
import * as Cmp from "@impero-com/common/utils/comparison";
import {
    ArrayDataSource,
    DataExtractor,
    DataView,
    getAllExtractors,
    find as DVFind,
    ExtractorExpander,
} from "@impero-com/common/utils/dataView";
import {
    formatFileNameForExport,
    formatUserName,
} from "@impero-com/common/utils/formatting";
import { t } from "@impero-com/common/utils/i18n";
import {
    addFieldFilter,
    DataViewIndex,
    QueryResult,
    removeFieldFilter,
    SimpleAndFilter,
    SimpleFieldFilter,
} from "@impero-com/common/utils/indexing";
import { assertNever } from "@impero-com/common/utils/obj";
import { OrgCookie } from "@impero-com/common/utils/orgCookie";
import * as XLSXUtils from "@impero-com/common/utils/xlsx";
import {
    actionMenuStatus,
    ActionSpec,
    Button,
    ContentWithoutBorder,
    emitNotification,
    InformationModal,
    PageSpinner,
    Tag,
} from "@impero-com/ui";
import { Col, Popover, Row } from "antd";
import { AxiosError } from "axios";
import memoizeOne from "memoize-one";
import { default as saveAs } from "save-as";

import type { UserProfile, Role } from "api/generated/model";
import {
    DetailedUserOverviewDtoContent,
    DetailedUserOverviewListDto as DetailedUsersOverview,
    UserAuthenticationStatus,
    ProvisionStatus,
    AvailableActions,
    GetUsersAssignmentCounts200,
    UpdatableUsers,
} from "api/generated/model";
import {
    getUsers,
    getUsersAssignmentCounts,
    patchUsers,
    postUsersInvite,
} from "api/generated/users/users";
import { LegacyRoute } from "legacyRoutes";
import { PageStateManager } from "org/pageState/components/pageStateManager";
import { pageStateUsersCodecs } from "org/pageState/convert/users";
import { IdAndName } from "org/pageState/models";
import { PAGE_KEY } from "org/pageState/pageKey";
import { PageFilterAndColumns } from "org/pageState/types";
import { splitPageStateWithCallback } from "org/pageState/utils";
import UserCreatorForm from "org/user/create";
import * as theme from "org/user/list/theme.css";
import { USER_SUBPATHS } from "org/user/userRoutes";
import { downgradeExtractorType } from "utils/dataView";
import { newExportMetadata } from "utils/export";

import { WebRoute } from "../../../routes";
import {
    getLocalizedRoleDescription,
    getLocalizedRoleName,
} from "../roleAssignment";

import AssignUsersToRoles from "./assignRoles";
import { exportUserList } from "./export";
import { USER_EXTRACTOR_KEYS } from "./extractorKeys";
import { UserModuleListApplicationBar, USERS_TAB } from "./listApplicationBar";
import { UserListResponseDetailed } from "./models";

function buildNameExtractor(
    users: DetailedUsersOverview
): DataExtractor<
    DetailedUserOverviewDtoContent,
    string,
    IdAndName<number, string>
> {
    const extractValue = (userOverview: DetailedUserOverviewDtoContent) =>
        formatUserName(userOverview.resource.user);
    const extractAndFormatValue = (user: DetailedUserOverviewDtoContent) =>
        formatUserName(user.resource.user);

    const mapIdToName: Record<number, string> = {};
    users.content.forEach((user) => {
        mapIdToName[user.resource.user.id] = formatUserName(user.resource.user);
    });

    return {
        id: USER_EXTRACTOR_KEYS.NAME,
        label: t("User_List_Column_Name"),
        getValue: extractValue,
        compare: Cmp.map(extractValue, Cmp.string()),
        asString: extractAndFormatValue,
        customFilterTransformer: {
            getValue: (users) => ({
                id: users.resource.user.id,
                name: formatUserName(users.resource.user),
            }),
            asString: (idAndName: IdAndName<number, string>) =>
                mapIdToName[idAndName.id] || idAndName.name,
        },
    };
}

function buildEmailExtractor(): DataExtractor<
    DetailedUserOverviewDtoContent,
    string
> {
    const extractValue = (userOverview: DetailedUserOverviewDtoContent) =>
        userOverview.resource.user.email;
    const formatValue = (email: string) => email;
    const extractAndFormatValue = (user: DetailedUserOverviewDtoContent) =>
        user.resource.user.email;

    return {
        id: USER_EXTRACTOR_KEYS.EMAIL,
        label: t("User_List_Column_Email"),
        getValue: extractValue,
        compare: Cmp.map(extractValue, Cmp.string()),
        asString: extractAndFormatValue,
        customFilterTransformer: {
            getValue: extractValue,
            asString: formatValue,
        },
    };
}

function buildLoginWithPasswordExtractor(): DataExtractor<
    DetailedUserOverviewDtoContent,
    LoginWithPassword,
    UserAuthenticationStatus
> {
    const extractValue = (userOverview: DetailedUserOverviewDtoContent) =>
        getLoginWithPassword(userOverview.resource.authenticationStatus);
    const formatValue = (filterValue: UserAuthenticationStatus) =>
        getLocalizedLoginWithPassword(getLoginWithPassword(filterValue));
    const extractAndFormatValue = (user: DetailedUserOverviewDtoContent) =>
        getLocalizedLoginWithPassword(
            getLoginWithPassword(user.resource.authenticationStatus)
        );

    return {
        id: USER_EXTRACTOR_KEYS.LOGIN_WITH_PASSWORD,
        label: t("User_List_Column_LoginWithPassword"),
        getValue: extractValue,
        compare: Cmp.map(extractValue, Cmp.string()),
        asString: extractAndFormatValue,
        customFilterTransformer: {
            getValue: (userOverview: DetailedUserOverviewDtoContent) =>
                userOverview.resource.authenticationStatus,
            asString: formatValue,
        },
    };
}

function buildRolesExtractor(): DataExtractor<
    DetailedUserOverviewDtoContent,
    Role[],
    Role
> {
    const extractRoles = (
        userOverview: DetailedUserOverviewDtoContent
    ): Role[] => userOverview.resource.roles || [];
    const countRoles = (userOverview: DetailedUserOverviewDtoContent): number =>
        extractRoles(userOverview).length;

    return {
        id: USER_EXTRACTOR_KEYS.ROLES,
        label: t("User_List_Column_Roles"),
        getValue: extractRoles,
        compare: Cmp.map(countRoles, Cmp.primitive),
        asString: (_userOverview) => "",
        customFilterTransformer: {
            getValue: (_userOverview: DetailedUserOverviewDtoContent) => {
                throw new Error(
                    "Unexpected call of customFilterTransformer.getValue() on tagCategories extractor."
                );
            },
            asString: getLocalizedRoleName,
        },
    };
}

function buildGroupsExtractor(
    assignmentCounts: AssignmentCounts
): DataExtractor<DetailedUserOverviewDtoContent, number> {
    const extractValue = (userOverview: DetailedUserOverviewDtoContent) =>
        getAssignmentGroups(assignmentCounts, userOverview);
    const formatValue = (count: number) => count.toString();
    const extractAndFormatValue = (user: DetailedUserOverviewDtoContent) =>
        getAssignmentGroups(assignmentCounts, user).toString();

    return {
        id: USER_EXTRACTOR_KEYS.GROUPS,
        label: t("User_Groups"),
        getValue: extractValue,
        compare: Cmp.map(extractValue, Cmp.primitive),
        asString: extractAndFormatValue,
        customFilterTransformer: {
            getValue: extractValue,
            asString: formatValue,
        },
    };
}

function buildDirectControlAssignmentsExtractor(
    assignmentCounts: AssignmentCounts
): DataExtractor<DetailedUserOverviewDtoContent, number> {
    const extractValue = (userOverview: DetailedUserOverviewDtoContent) =>
        getAssignmentDirectControlAssignment(assignmentCounts, userOverview);
    const formatValue = (count: number) => count.toString();
    const extractAndFormatValue = (user: DetailedUserOverviewDtoContent) =>
        getAssignmentDirectControlAssignment(assignmentCounts, user).toString();

    return {
        id: USER_EXTRACTOR_KEYS.DIRECT_CONTROL_ASSIGNMENTS,
        label: t("User_Controls"),
        getValue: extractValue,
        compare: Cmp.map(extractValue, Cmp.primitive),
        asString: extractAndFormatValue,
        customFilterTransformer: {
            getValue: extractValue,
            asString: formatValue,
        },
    };
}

function buildProgramsExtractor(
    assignmentCounts: AssignmentCounts
): DataExtractor<DetailedUserOverviewDtoContent, number> {
    const extractValue = (userOverview: DetailedUserOverviewDtoContent) =>
        getAssignmentPrograms(assignmentCounts, userOverview);
    const formatValue = (count: number) => count.toString();
    const extractAndFormatValue = (user: DetailedUserOverviewDtoContent) =>
        getAssignmentPrograms(assignmentCounts, user).toString();

    return {
        id: USER_EXTRACTOR_KEYS.PROGRAMS,
        label: t("User_ControlPrograms"),
        getValue: extractValue,
        compare: Cmp.map(extractValue, Cmp.primitive),
        asString: extractAndFormatValue,
        customFilterTransformer: {
            getValue: extractValue,
            asString: formatValue,
        },
    };
}

function buildPendingActivitiesExtractor(
    assignmentCounts: AssignmentCounts
): DataExtractor<DetailedUserOverviewDtoContent, number> {
    const extractValue = (userOverview: DetailedUserOverviewDtoContent) =>
        getAssignmentPendingActivities(assignmentCounts, userOverview);
    const formatValue = (count: number) => count.toString();
    const extractAndFormatValue = (user: DetailedUserOverviewDtoContent) =>
        getAssignmentPendingActivities(assignmentCounts, user).toString();

    return {
        id: USER_EXTRACTOR_KEYS.PENDING_ACTIVITIES,
        label: t("User_PendingActivities"),
        getValue: extractValue,
        compare: Cmp.map(extractValue, Cmp.primitive),
        asString: extractAndFormatValue,
        customFilterTransformer: {
            getValue: extractValue,
            asString: formatValue,
        },
    };
}

function buildTestingControlAssignmentsExtractor(
    assignmentCounts: AssignmentCounts
): DataExtractor<DetailedUserOverviewDtoContent, number> {
    const extractValue = (userOverview: DetailedUserOverviewDtoContent) =>
        getAssignmentTestingControlAssignment(assignmentCounts, userOverview);
    const formatValue = (count: number) => count.toString();
    const extractAndFormatValue = (user: DetailedUserOverviewDtoContent) =>
        getAssignmentTestingControlAssignment(
            assignmentCounts,
            user
        ).toString();

    return {
        id: USER_EXTRACTOR_KEYS.TESTING_CONTROL_ASSIGNMENTS,
        label: t("User_TestingControls"),
        getValue: extractValue,
        compare: Cmp.map(extractValue, Cmp.primitive),
        asString: extractAndFormatValue,
        customFilterTransformer: {
            getValue: extractValue,
            asString: formatValue,
        },
    };
}

function buildTestingProgramsExtractor(
    assignmentCounts: AssignmentCounts
): DataExtractor<DetailedUserOverviewDtoContent, number> {
    const extractValue = (userOverview: DetailedUserOverviewDtoContent) =>
        getAssignmentTestingPrograms(assignmentCounts, userOverview);
    const formatValue = (count: number) => count.toString();
    const extractAndFormatValue = (user: DetailedUserOverviewDtoContent) =>
        getAssignmentTestingPrograms(assignmentCounts, user).toString();

    return {
        id: USER_EXTRACTOR_KEYS.TESTING_PROGRAMS,
        label: t("User_TestingPrograms"),
        getValue: extractValue,
        compare: Cmp.map(extractValue, Cmp.primitive),
        asString: extractAndFormatValue,
        customFilterTransformer: {
            getValue: extractValue,
            asString: formatValue,
        },
    };
}

function buildPendingTestingActivitiesExtractor(
    assignmentCounts: AssignmentCounts
): DataExtractor<DetailedUserOverviewDtoContent, number> {
    const extractValue = (userOverview: DetailedUserOverviewDtoContent) =>
        getAssignmentPendingTestingActivities(assignmentCounts, userOverview);
    const formatValue = (count: number) => count.toString();
    const extractAndFormatValue = (user: DetailedUserOverviewDtoContent) =>
        getAssignmentPendingTestingActivities(
            assignmentCounts,
            user
        ).toString();

    return {
        id: USER_EXTRACTOR_KEYS.PENDING_TESTING_ACTIVITIES,
        label: t("User_TestingActivities"),
        getValue: extractValue,
        compare: Cmp.map(extractValue, Cmp.primitive),
        asString: extractAndFormatValue,
        customFilterTransformer: {
            getValue: extractValue,
            asString: formatValue,
        },
    };
}

function buildReportsExtractor(
    assignmentCounts: AssignmentCounts
): DataExtractor<DetailedUserOverviewDtoContent, number> {
    const extractValue = (userOverview: DetailedUserOverviewDtoContent) =>
        getAssignmentCountReports(assignmentCounts, userOverview);
    const formatValue = (count: number) => count.toString();
    const extractAndFormatValue = (user: DetailedUserOverviewDtoContent) =>
        getAssignmentCountReports(assignmentCounts, user).toString();

    return {
        id: USER_EXTRACTOR_KEYS.REPORTS,
        label: t("Report_MenuTitle"),
        getValue: extractValue,
        compare: Cmp.map(extractValue, Cmp.primitive),
        asString: extractAndFormatValue,
        customFilterTransformer: {
            getValue: extractValue,
            asString: formatValue,
        },
    };
}

function buildRiskManagementExtractor(
    assignmentCounts: AssignmentCounts
): DataExtractor<DetailedUserOverviewDtoContent, number> {
    const extractValue = (userOverview: DetailedUserOverviewDtoContent) =>
        getAssignmentRisks(assignmentCounts, userOverview);
    const formatValue = (count: number) => count.toString();
    const extractAndFormatValue = (user: DetailedUserOverviewDtoContent) =>
        getAssignmentRisks(assignmentCounts, user).toString();

    return {
        id: USER_EXTRACTOR_KEYS.RISK_MANAGEMENT,
        label: t("RiskManagement_MenuTitle"),
        getValue: extractValue,
        compare: Cmp.map(extractValue, Cmp.primitive),
        asString: extractAndFormatValue,
        customFilterTransformer: {
            getValue: extractValue,
            asString: formatValue,
        },
    };
}

function buildProvisionStatusExtractor(): DataExtractor<
    DetailedUserOverviewDtoContent,
    ProvisionStatus | null
> {
    const extractValue = (userOverview: DetailedUserOverviewDtoContent) =>
        userOverview.resource.provisionStatus ?? null;
    const formatValue = (status: ProvisionStatus | null) => {
        switch (status) {
            case "provisioned":
                return t("User_List_ProvisionStatus_Provisioned");
            case "markedForDeletion":
                return t("User_List_ProvisionStatus_MarkedForDeletion");
            case null:
                return "";
            default:
                assertNever(status);
        }
    };
    const extractAndFormatValue = (
        userOverview: DetailedUserOverviewDtoContent
    ) => formatValue(extractValue(userOverview));

    return {
        id: USER_EXTRACTOR_KEYS.PROVISION_STATUS,
        label: t("User_List_Column_ProvisionStatus"),
        getValue: extractValue,
        compare: Cmp.map(extractAndFormatValue, Cmp.primitive),
        asString: extractAndFormatValue,
        customFilterTransformer: {
            getValue: extractValue,
            asString: formatValue,
        },
    };
}

interface UserListProps {
    ajaxManager: AjaxManager;
    orgCookie: OrgCookie;
    orgProfileCookie: UserProfile | null;
    availableActions: AvailableActions;
    controlTestingEnabled: boolean;
    controlEntitiesEnabled: boolean;
    mcpEnabled: boolean;
    scimEnabled: boolean;
}

type DialogState =
    | {
          type: "CreateUser";
      }
    | {
          type: "SendInvite";
          users: Array<number>;
          processing: boolean;
      }
    | {
          type: "AssignToRole";
          users: Array<number>;
          processing: boolean;
      }
    | undefined;

interface UserListState {
    dialog: DialogState;
}

const INVITE_PENDING = "InvitePending";
const NOT_INVITED = "NotInvited";
const ALLOWED = "Allowed";
const NOT_ALLOWED = "NotAllowed";

type AssignmentCounts = GetUsersAssignmentCounts200;

type LoginWithPassword =
    | typeof INVITE_PENDING
    | typeof NOT_INVITED
    | typeof ALLOWED
    | typeof NOT_ALLOWED;

function getLocalizedLoginWithPassword(value: LoginWithPassword): string {
    switch (value) {
        case INVITE_PENDING:
            return t("User_LoginWithPassword_InvitePending");
        case NOT_INVITED:
            return t("User_LoginWithPassword_NotInvited");
        case ALLOWED:
            return t("User_LoginWithPassword_Allowed");
        case NOT_ALLOWED:
            return t("User_LoginWithPassword_NotAllowed");
        default:
            return assertNever(value);
    }
}

function getLoginWithPassword(
    authStatus: UserAuthenticationStatus
): LoginWithPassword {
    const { hasPassword, passwordAuthenticationDisabled, inviteSent } =
        authStatus;

    if (passwordAuthenticationDisabled) {
        return NOT_ALLOWED;
    }
    if (hasPassword) {
        return ALLOWED;
    }
    if (inviteSent) {
        return INVITE_PENDING;
    }

    return NOT_INVITED;
}

function shouldDisplayRoles(
    dataView: DataView<DetailedUserOverviewDtoContent>
): boolean {
    return (
        DVFind(
            dataView.dataSource.iter(),
            (userOverview) => userOverview.resource.roles === null
        ) === undefined
    );
}

function getAssignmentCountReports(
    assignmentCounts: AssignmentCounts,
    userOverview: DetailedUserOverviewDtoContent
): number {
    const assignmentCount = assignmentCounts[userOverview.resource.user.id];
    if (assignmentCount) {
        return (
            assignmentCount.savedReports +
            assignmentCount.pushReports.responsible +
            assignmentCount.pushReports.recipient
        );
    } else {
        return 0;
    }
}

function getAssignmentPendingActivities(
    assignmentCounts: AssignmentCounts,
    userOverview: DetailedUserOverviewDtoContent
): number {
    const assignmentCount = assignmentCounts[userOverview.resource.user.id];
    if (assignmentCount) {
        return (
            assignmentCount.controlResults.direct +
            assignmentCount.controlResults.pool
        );
    } else {
        return 0;
    }
}

function getAssignmentPrograms(
    assignmentCounts: AssignmentCounts,
    userOverview: DetailedUserOverviewDtoContent
): number {
    const assignmentCount = assignmentCounts[userOverview.resource.user.id];
    if (assignmentCount) {
        return assignmentCount.controlPrograms;
    } else {
        return 0;
    }
}

function getAssignmentDirectControlAssignment(
    assignmentCounts: AssignmentCounts,
    userOverview: DetailedUserOverviewDtoContent
): number {
    const assignmentCount = assignmentCounts[userOverview.resource.user.id];
    if (assignmentCount) {
        return assignmentCount.controlAssignments.direct;
    } else {
        return 0;
    }
}

function getAssignmentGroups(
    assignmentCounts: AssignmentCounts,
    userOverview: DetailedUserOverviewDtoContent
): number {
    const assignmentCount = assignmentCounts[userOverview.resource.user.id];
    if (assignmentCount) {
        return assignmentCount.groups;
    } else {
        return 0;
    }
}

function getAssignmentPendingTestingActivities(
    assignmentCounts: AssignmentCounts,
    userOverview: DetailedUserOverviewDtoContent
): number {
    const assignmentCount = assignmentCounts[userOverview.resource.user.id];
    if (assignmentCount) {
        return assignmentCount.controlTesting.activities;
    } else {
        return 0;
    }
}

function getAssignmentTestingPrograms(
    assignmentCounts: AssignmentCounts,
    userOverview: DetailedUserOverviewDtoContent
): number {
    const assignmentCount = assignmentCounts[userOverview.resource.user.id];
    if (assignmentCount) {
        return assignmentCount.controlTesting.programs;
    } else {
        return 0;
    }
}

function getAssignmentTestingControlAssignment(
    assignmentCounts: AssignmentCounts,
    userOverview: DetailedUserOverviewDtoContent
): number {
    const assignmentCount = assignmentCounts[userOverview.resource.user.id];
    if (assignmentCount) {
        return assignmentCount.testingControlAssignments.direct;
    } else {
        return 0;
    }
}

function getAssignmentRisks(
    assignmentCounts: AssignmentCounts,
    userOverview: DetailedUserOverviewDtoContent
): number {
    const assignmentCount = assignmentCounts[userOverview.resource.user.id];
    if (assignmentCount) {
        return assignmentCount.entities.risk + assignmentCount.riskDirectories;
    } else {
        return 0;
    }
}

class UserList extends React.PureComponent<UserListProps, UserListState> {
    private asyncComponent =
        React.createRef<
            AsyncComponent2<UserListResponseDetailed, AssignmentCounts>
        >();
    private assignUsers = React.createRef<typeof AssignUsersToRoles>();

    state: UserListState = {
        dialog: undefined,
    };

    private memoizeDataView: (
        detailedUsers: DetailedUsersOverview,
        assignmentCounts: AssignmentCounts,
        controlTestingEnabled: boolean,
        scimEnabled: boolean
    ) => [
        DataView<DetailedUserOverviewDtoContent>,
        DataViewIndex<DetailedUserOverviewDtoContent>,
    ] = memoizeOne(
        (
            detailedUsers: DetailedUsersOverview,
            assignmentCounts: AssignmentCounts,
            controlTestingEnabled: boolean,
            scimEnabled: boolean
        ) => {
            const standardExtractors: Array<
                DataExtractor<DetailedUserOverviewDtoContent, unknown>
            > = [
                downgradeExtractorType(buildNameExtractor(detailedUsers)),
                downgradeExtractorType(buildEmailExtractor()),
                downgradeExtractorType(buildLoginWithPasswordExtractor()),
                downgradeExtractorType(buildRolesExtractor()),
                downgradeExtractorType(buildGroupsExtractor(assignmentCounts)),
                downgradeExtractorType(
                    buildDirectControlAssignmentsExtractor(assignmentCounts)
                ),
                downgradeExtractorType(
                    buildProgramsExtractor(assignmentCounts)
                ),
                downgradeExtractorType(
                    buildPendingActivitiesExtractor(assignmentCounts)
                ),
                downgradeExtractorType(buildReportsExtractor(assignmentCounts)),
                downgradeExtractorType(
                    buildRiskManagementExtractor(assignmentCounts)
                ),
                ...(controlTestingEnabled
                    ? [
                          downgradeExtractorType(
                              buildTestingControlAssignmentsExtractor(
                                  assignmentCounts
                              )
                          ),
                          downgradeExtractorType(
                              buildTestingProgramsExtractor(assignmentCounts)
                          ),
                          downgradeExtractorType(
                              buildPendingTestingActivitiesExtractor(
                                  assignmentCounts
                              )
                          ),
                      ]
                    : []),
                ...(scimEnabled
                    ? [downgradeExtractorType(buildProvisionStatusExtractor())]
                    : []),
            ];

            const dataView: DataView<DetailedUserOverviewDtoContent> = {
                dataSource: new ArrayDataSource(detailedUsers.content, (user) =>
                    user.resource.user.id.toString()
                ),
                extractorGroups: [
                    {
                        id: "standard",
                        label: "Standard",
                        extractors: standardExtractors,
                    },
                ],
                sorters: [],
                defaultCompareItems: Cmp.map(
                    (user) => user.resource.user.id,
                    Cmp.primitive
                ),
            };

            const expanders: ExtractorExpander<Array<Role>, Role>[] = [
                {
                    extractorId: "roles",
                    expand: (roles: Array<Role>) =>
                        roles.map((role) => {
                            return {
                                partialId: `role-${role}`,
                                expandedValue: role,
                            };
                        }),
                    asString: (role) => getLocalizedRoleName(role),
                    compare: Cmp.string(),
                    customFilterTransformer: {
                        getValue: (role: Role) => role,
                        asString: getLocalizedRoleName,
                    },
                },
            ];

            return [
                dataView,
                new DataViewIndex(dataView, undefined, expanders),
            ];
        }
    );

    private buildQueryResult = memoizeOne(
        (
            dataView: DataView<DetailedUserOverviewDtoContent>,
            dataViewIndex: DataViewIndex<DetailedUserOverviewDtoContent>,
            filter: SimpleAndFilter | null
        ): QueryResult<DetailedUserOverviewDtoContent> => {
            if (filter) {
                return dataViewIndex.find(filter);
            } else {
                return {
                    dataView,
                    orResults: [],
                    excludedItemIds: null,
                };
            }
        }
    );

    private renderDialog(detailedUsers: UserListResponseDetailed) {
        const { dialog } = this.state;
        const { ajaxManager, availableActions, orgProfileCookie } = this.props;
        if (dialog === undefined) {
            return null;
        }

        switch (dialog.type) {
            case "CreateUser":
                return (
                    <UserCreatorForm
                        onUserCreated={(_user) => {
                            this.asyncComponent.current?.reload();
                            this.setState({ dialog: undefined });
                        }}
                        onCancel={() => {
                            this.setState({ dialog: undefined });
                        }}
                        availableActions={availableActions}
                        ajaxManager={ajaxManager}
                        orgProfileCookie={orgProfileCookie}
                    />
                );
            case "SendInvite":
                const inviteTitle = t("User_List_Action_SendInvite_Title", {
                    count: dialog.users.length,
                });
                return (
                    <InformationModal
                        open
                        onClose={() => this.setState({ dialog: undefined })}
                        type="info"
                        icon="send"
                        title={inviteTitle}
                        footer={
                            <>
                                <Button
                                    onClick={() =>
                                        this.setState({ dialog: undefined })
                                    }
                                >
                                    {t("Cancel")}
                                </Button>
                                <Button
                                    variant="primary"
                                    loading={dialog.processing}
                                    onClick={() => {
                                        this.setState({
                                            dialog: {
                                                type: "SendInvite",
                                                users: dialog.users,
                                                processing: true,
                                            },
                                        });
                                        postUsersInvite(dialog.users)
                                            .then(() => {
                                                const description = t(
                                                    "User_List_Action_SendInvite_Confirm",
                                                    {
                                                        count: dialog.users
                                                            .length,
                                                    }
                                                );
                                                emitNotification(
                                                    "success",
                                                    description
                                                );
                                                this.setState({
                                                    dialog: undefined,
                                                });
                                                this.asyncComponent.current?.reload();
                                            })
                                            .catch((err: AxiosError) => {
                                                emitNotification(
                                                    "error",
                                                    err.message
                                                );
                                                this.setState({
                                                    dialog: undefined,
                                                });
                                            });
                                    }}
                                >
                                    {inviteTitle}
                                </Button>
                            </>
                        }
                    >
                        <p>
                            {t("User_List_Action_SendInvite_Body", {
                                count: dialog.users.length,
                            })}
                        </p>
                    </InformationModal>
                );
            case "AssignToRole":
                const selectedMap = new Map();

                detailedUsers.data.content
                    .filter(
                        (u) =>
                            dialog.users.findIndex(
                                (su) => su === u.resource.user.id
                            ) >= 0
                    )
                    .forEach((user) =>
                        selectedMap.set(
                            user.resource.user.id,
                            new Set(user.resource.roles)
                        )
                    );

                return (
                    <AssignUsersToRoles
                        assignableRoles={availableActions.assignableRoles}
                        processing={dialog.processing}
                        userRolesMap={selectedMap}
                        controlTestingEnabled={this.props.controlTestingEnabled}
                        controlEntitiesEnabled={
                            this.props.controlEntitiesEnabled
                        }
                        mcpEnabled={this.props.mcpEnabled}
                        onAssignRoles={(data: UpdatableUsers) => {
                            this.setState({
                                dialog: { ...dialog, processing: true },
                            });
                            patchUsers(data)
                                .then(() => {
                                    this.setState({ dialog: undefined });
                                    this.asyncComponent.current?.reload();
                                })
                                .catch((err: AxiosError) => {
                                    this.setState({ dialog: undefined });
                                    emitNotification("error", err.message);
                                });
                        }}
                        onCancel={() => {
                            this.setState({ dialog: undefined });
                        }}
                    />
                );
            default:
                return assertNever(dialog);
        }
    }

    private getActions(
        availableActions: AvailableActions,
        forceDisable: boolean
    ): ActionSpec[] {
        return [
            {
                id: "create-user",
                label: t("User_CreateTitle"),
                icon: { svg: createIcon },
                onClick: () => {
                    this.setState({ dialog: { type: "CreateUser" } });
                },
                status: actionMenuStatus(
                    !forceDisable && availableActions.user.user.create,
                    t("User_Create_Forbidden")
                ),
            },
            {
                id: "create-multiple-users",
                label: t("User_Create_Multiple"),
                icon: { svg: createIcon },
                link: { href: LegacyRoute.USER_BULK },
                status: actionMenuStatus(
                    !forceDisable && availableActions.user.user.create,
                    t("User_Create_Forbidden")
                ),
            },
        ];
    }

    render = () => {
        const {
            ajaxManager,
            availableActions,
            controlTestingEnabled,
            scimEnabled,
        } = this.props;

        const renderError = (code: number | undefined) => (
            <UserModuleListApplicationBar
                actions={this.getActions(availableActions, true)}
                currentTabId={USERS_TAB}
                shareableLink={{
                    state: "inactive",
                    reason:
                        code === 403
                            ? t("ShareableLink_CreateNew_Forbidden")
                            : getErrorPageTitle(code),
                }}
            >
                {code === 403 ? (
                    <NoAccessCard />
                ) : (
                    <LoggedInHTTPError code={code} />
                )}
            </UserModuleListApplicationBar>
        );
        const renderLoading = () => <PageSpinner />;

        return (
            <AsyncComponent2<UserListResponseDetailed, AssignmentCounts>
                ajaxManager={ajaxManager}
                ref={this.asyncComponent}
                promises={[
                    () =>
                        getUsers({ format: "Detailed" }).then((response) => {
                            if (response.data.type !== "Detailed") {
                                throw new Error(
                                    "Expected a detailed user list"
                                );
                            }
                            return {
                                ...response,
                                data: response.data,
                            };
                        }),
                    getUsersAssignmentCounts,
                ]}
                key="userList"
                _overrideErrorHandlers={[403]}
                renderLoading={renderLoading}
                renderError={renderError}
                renderContent={(
                    detailedUsers: UserListResponseDetailed,
                    assignmentCounts: AssignmentCounts
                ) => {
                    return (
                        <PageStateManager<"Users", PageFilterAndColumns>
                            pageKey={PAGE_KEY.users()}
                            renderLoading={renderLoading}
                            renderError={renderError}
                            codecs={pageStateUsersCodecs}
                            renderContent={({
                                pageState,
                                onPageStateChange,
                                onCreateShareableLink,
                            }) => {
                                const {
                                    filter,
                                    visibleColumnIds,
                                    onFilterChange,
                                    onVisibleColumnsChange,
                                } = splitPageStateWithCallback(
                                    pageState,
                                    onPageStateChange
                                );
                                const { data } = detailedUsers;
                                const [dataView, dataViewIndex] =
                                    this.memoizeDataView(
                                        data,
                                        assignmentCounts,
                                        controlTestingEnabled,
                                        scimEnabled
                                    );
                                const queryResult = this.buildQueryResult(
                                    dataView,
                                    dataViewIndex,
                                    filter
                                );
                                const displayRoles =
                                    shouldDisplayRoles(dataView);

                                return (
                                    <UserModuleListApplicationBar
                                        actions={this.getActions(
                                            availableActions,
                                            false
                                        )}
                                        currentTabId={USERS_TAB}
                                        shareableLink={{
                                            state: "active",
                                            onCreate: onCreateShareableLink,
                                        }}
                                    >
                                        <ContentWithoutBorder
                                            className={theme.whiteBg}
                                        >
                                            <TableFilter<DetailedUserOverviewDtoContent>
                                                id="filter"
                                                queryResult={queryResult}
                                                dataViewIndex={dataViewIndex}
                                                filter={filter}
                                                extractorRefs={getAllExtractors(
                                                    dataView
                                                ).map((ext) => ({
                                                    extractorId: ext.id,
                                                }))}
                                                onAddFilter={this.addFilter(
                                                    filter,
                                                    onFilterChange
                                                )}
                                                onRemoveFilter={this.removeFilter(
                                                    filter,
                                                    onFilterChange
                                                )}
                                                onResetFilter={this.resetFilter(
                                                    filter,
                                                    onFilterChange
                                                )}
                                            />
                                            <DataTable
                                                id="user-list"
                                                mode={DATA_TABLE_MODE_VIEWPORT}
                                                onVisibleColumnsChanged={
                                                    onVisibleColumnsChange
                                                }
                                                visibleColumnIds={
                                                    visibleColumnIds
                                                }
                                                selectionHandler={{
                                                    tools: [
                                                        {
                                                            isEnabled: (
                                                                _formattedView,
                                                                selectedIds,
                                                                _visibleColumns
                                                            ) =>
                                                                this.enableSendInvite(
                                                                    selectedIds,
                                                                    detailedUsers.data
                                                                ),
                                                            type: "Text",
                                                            label: t(
                                                                "User_List_Action_SendInvite_Title",
                                                                {
                                                                    count: 1,
                                                                }
                                                            ), // Ideally, this should react to how many selectedIds we have. We don't expose that currently.
                                                            onCommand:
                                                                this
                                                                    .onSendInvite,
                                                        },
                                                        {
                                                            isEnabled:
                                                                this
                                                                    .enableAssignRole,
                                                            type: "Text",
                                                            label: t(
                                                                "User_List_Action_AssignToRole"
                                                            ),
                                                            onCommand:
                                                                this
                                                                    .onAssignRole,
                                                        },
                                                        {
                                                            type: "Text",
                                                            label: t(
                                                                "User_List_Action_Export"
                                                            ),
                                                            onCommand:
                                                                this
                                                                    .onExportUsers,
                                                        },
                                                    ],
                                                }}
                                                tools={[
                                                    {
                                                        type: "Spacer",
                                                        flex: "auto",
                                                    },
                                                    {
                                                        type: "ViewAdjuster",
                                                    },
                                                ]}
                                                dataView={queryResult.dataView}
                                                columnBuilders={{
                                                    [USER_EXTRACTOR_KEYS.GROUPS]:
                                                        (
                                                            extractor: DataExtractor<
                                                                DetailedUserOverviewDtoContent,
                                                                string
                                                            >
                                                        ) => ({
                                                            getFormattedValue: (
                                                                row: DetailedUserOverviewDtoContent
                                                            ) => {
                                                                const count =
                                                                    extractor.getValue(
                                                                        row
                                                                    )!;
                                                                const userId =
                                                                    row.resource
                                                                        .user
                                                                        .id;
                                                                const hasAccess =
                                                                    row.actions
                                                                        .edit;
                                                                return (
                                                                    <ProtectedLink
                                                                        to={`${WebRoute.USERS}/${userId}/${USER_SUBPATHS.GROUPS.path}`}
                                                                        hasAccess={
                                                                            hasAccess
                                                                        }
                                                                    >
                                                                        {count}
                                                                    </ProtectedLink>
                                                                );
                                                            },
                                                            layout: {
                                                                fixedWidth: 100,
                                                            },
                                                        }),
                                                    [USER_EXTRACTOR_KEYS.PROGRAMS]:
                                                        (
                                                            extractor: DataExtractor<
                                                                DetailedUserOverviewDtoContent,
                                                                string
                                                            >
                                                        ) => ({
                                                            getFormattedValue: (
                                                                row: DetailedUserOverviewDtoContent
                                                            ) => {
                                                                const count =
                                                                    extractor.getValue(
                                                                        row
                                                                    )!;
                                                                const userId =
                                                                    row.resource
                                                                        .user
                                                                        .id;
                                                                const hasAccess =
                                                                    row.actions
                                                                        .edit;
                                                                return (
                                                                    <ProtectedLink
                                                                        to={`${WebRoute.USERS}/${userId}/${USER_SUBPATHS.PROGRAMS.path}`}
                                                                        hasAccess={
                                                                            hasAccess
                                                                        }
                                                                    >
                                                                        {count}
                                                                    </ProtectedLink>
                                                                );
                                                            },
                                                            layout: {
                                                                fixedWidth: 100,
                                                            },
                                                        }),
                                                    [USER_EXTRACTOR_KEYS.DIRECT_CONTROL_ASSIGNMENTS]:
                                                        (
                                                            extractor: DataExtractor<
                                                                DetailedUserOverviewDtoContent,
                                                                string
                                                            >
                                                        ) => ({
                                                            getFormattedValue: (
                                                                row: DetailedUserOverviewDtoContent
                                                            ) => {
                                                                const count =
                                                                    extractor.getValue(
                                                                        row
                                                                    )!;
                                                                const userId =
                                                                    row.resource
                                                                        .user
                                                                        .id;
                                                                const hasAccess =
                                                                    row.actions
                                                                        .edit;
                                                                return (
                                                                    <ProtectedLink
                                                                        to={`${WebRoute.USERS}/${userId}/${USER_SUBPATHS.CONTROLS.path}`}
                                                                        hasAccess={
                                                                            hasAccess
                                                                        }
                                                                    >
                                                                        {count}
                                                                    </ProtectedLink>
                                                                );
                                                            },
                                                            layout: {
                                                                fixedWidth: 150,
                                                            },
                                                        }),
                                                    [USER_EXTRACTOR_KEYS.PENDING_ACTIVITIES]:
                                                        (
                                                            extractor: DataExtractor<
                                                                DetailedUserOverviewDtoContent,
                                                                string
                                                            >
                                                        ) => ({
                                                            getFormattedValue: (
                                                                row: DetailedUserOverviewDtoContent
                                                            ) => {
                                                                const count =
                                                                    extractor.getValue(
                                                                        row
                                                                    )!;
                                                                const userId =
                                                                    row.resource
                                                                        .user
                                                                        .id;
                                                                const hasAccess =
                                                                    row.actions
                                                                        .edit;
                                                                return (
                                                                    <ProtectedLink
                                                                        to={`${WebRoute.USERS}/${userId}/${USER_SUBPATHS.DIRECT_CONTROL_RESULTS.path}`}
                                                                        hasAccess={
                                                                            hasAccess
                                                                        }
                                                                    >
                                                                        {count}
                                                                    </ProtectedLink>
                                                                );
                                                            },
                                                            layout: {
                                                                fixedWidth: 100,
                                                            },
                                                        }),
                                                    [USER_EXTRACTOR_KEYS.TESTING_CONTROL_ASSIGNMENTS]:
                                                        (
                                                            extractor: DataExtractor<
                                                                DetailedUserOverviewDtoContent,
                                                                string
                                                            >
                                                        ) => ({
                                                            getFormattedValue: (
                                                                row: DetailedUserOverviewDtoContent
                                                            ) => {
                                                                const count =
                                                                    extractor.getValue(
                                                                        row
                                                                    )!;
                                                                const userId =
                                                                    row.resource
                                                                        .user
                                                                        .id;
                                                                const hasAccess =
                                                                    row.actions
                                                                        .edit;
                                                                return (
                                                                    <ProtectedLink
                                                                        to={`${WebRoute.USERS}/${userId}/${USER_SUBPATHS.TESTING_CONTROLS.path}`}
                                                                        hasAccess={
                                                                            hasAccess
                                                                        }
                                                                    >
                                                                        {count}
                                                                    </ProtectedLink>
                                                                );
                                                            },
                                                            layout: {
                                                                fixedWidth: 100,
                                                            },
                                                        }),
                                                    [USER_EXTRACTOR_KEYS.TESTING_PROGRAMS]:
                                                        (
                                                            extractor: DataExtractor<
                                                                DetailedUserOverviewDtoContent,
                                                                string
                                                            >
                                                        ) => ({
                                                            getFormattedValue: (
                                                                row: DetailedUserOverviewDtoContent
                                                            ) => {
                                                                const count =
                                                                    extractor.getValue(
                                                                        row
                                                                    )!;
                                                                const userId =
                                                                    row.resource
                                                                        .user
                                                                        .id;
                                                                const hasAccess =
                                                                    row.actions
                                                                        .edit;
                                                                return (
                                                                    <ProtectedLink
                                                                        to={`${WebRoute.USERS}/${userId}/${USER_SUBPATHS.TESTING_PROGRAMS.path}`}
                                                                        hasAccess={
                                                                            hasAccess
                                                                        }
                                                                    >
                                                                        {count}
                                                                    </ProtectedLink>
                                                                );
                                                            },
                                                            layout: {
                                                                fixedWidth: 100,
                                                            },
                                                        }),
                                                    [USER_EXTRACTOR_KEYS.PENDING_TESTING_ACTIVITIES]:
                                                        (
                                                            extractor: DataExtractor<
                                                                DetailedUserOverviewDtoContent,
                                                                string
                                                            >
                                                        ) => ({
                                                            getFormattedValue: (
                                                                row: DetailedUserOverviewDtoContent
                                                            ) => {
                                                                const count =
                                                                    extractor.getValue(
                                                                        row
                                                                    )!;
                                                                const userId =
                                                                    row.resource
                                                                        .user
                                                                        .id;
                                                                const hasAccess =
                                                                    row.actions
                                                                        .edit;
                                                                return (
                                                                    <ProtectedLink
                                                                        to={`${WebRoute.USERS}/${userId}/${USER_SUBPATHS.TESTING_ACTIVITIES.path}`}
                                                                        hasAccess={
                                                                            hasAccess
                                                                        }
                                                                    >
                                                                        {count}
                                                                    </ProtectedLink>
                                                                );
                                                            },
                                                            layout: {
                                                                fixedWidth: 100,
                                                            },
                                                        }),
                                                    [USER_EXTRACTOR_KEYS.REPORTS]:
                                                        (
                                                            extractor: DataExtractor<
                                                                DetailedUserOverviewDtoContent,
                                                                string
                                                            >
                                                        ) => ({
                                                            getFormattedValue: (
                                                                row: DetailedUserOverviewDtoContent
                                                            ) => {
                                                                const count =
                                                                    extractor.getValue(
                                                                        row
                                                                    )!;
                                                                const userId =
                                                                    row.resource
                                                                        .user
                                                                        .id;
                                                                const hasAccess =
                                                                    row.actions
                                                                        .edit;
                                                                return (
                                                                    <ProtectedLink
                                                                        to={`${WebRoute.USERS}/${userId}/${USER_SUBPATHS.CUSTOM_REPORTS.path}`}
                                                                        hasAccess={
                                                                            hasAccess
                                                                        }
                                                                    >
                                                                        {count}
                                                                    </ProtectedLink>
                                                                );
                                                            },
                                                            layout: {
                                                                fixedWidth: 100,
                                                            },
                                                        }),
                                                    [USER_EXTRACTOR_KEYS.RISK_MANAGEMENT]:
                                                        (
                                                            extractor: DataExtractor<
                                                                DetailedUserOverviewDtoContent,
                                                                string
                                                            >
                                                        ) => ({
                                                            getFormattedValue: (
                                                                row: DetailedUserOverviewDtoContent
                                                            ) => {
                                                                const count =
                                                                    extractor.getValue(
                                                                        row
                                                                    )!;
                                                                const userId =
                                                                    row.resource
                                                                        .user
                                                                        .id;
                                                                const hasAccess =
                                                                    row.actions
                                                                        .edit;
                                                                return (
                                                                    <ProtectedLink
                                                                        to={`${WebRoute.USERS}/${userId}/${USER_SUBPATHS.RISK_ENTITIES.path}`}
                                                                        hasAccess={
                                                                            hasAccess
                                                                        }
                                                                    >
                                                                        {count}
                                                                    </ProtectedLink>
                                                                );
                                                            },
                                                            layout: {
                                                                fixedWidth: 100,
                                                            },
                                                        }),
                                                    [USER_EXTRACTOR_KEYS.ROLES]:
                                                        (
                                                            extractor: DataExtractor<
                                                                DetailedUserOverviewDtoContent,
                                                                Array<Role>
                                                            >
                                                        ) => ({
                                                            getFormattedValue: (
                                                                row: DetailedUserOverviewDtoContent
                                                            ) => {
                                                                return (
                                                                    <Row
                                                                        justify="start"
                                                                        gutter={[
                                                                            6,
                                                                            6,
                                                                        ]}
                                                                    >
                                                                        {extractor
                                                                            .getValue(
                                                                                row
                                                                            )
                                                                            ?.map(
                                                                                (
                                                                                    role,
                                                                                    idx
                                                                                ) =>
                                                                                    roleTag(
                                                                                        role,
                                                                                        idx
                                                                                    )
                                                                            ) ??
                                                                            []}
                                                                    </Row>
                                                                );
                                                            },
                                                            display:
                                                                displayRoles
                                                                    ? "DEFAULT_VISIBLE"
                                                                    : "NEVER",
                                                            layout: {
                                                                fixedWidth: 300,
                                                            },
                                                        }),
                                                    [USER_EXTRACTOR_KEYS.NAME]:
                                                        (
                                                            extractor: DataExtractor<
                                                                DetailedUserOverviewDtoContent,
                                                                string
                                                            >
                                                        ) => ({
                                                            getFormattedValue: (
                                                                row: DetailedUserOverviewDtoContent
                                                            ) => {
                                                                const name =
                                                                    extractor.getValue(
                                                                        row
                                                                    )!;
                                                                const userId =
                                                                    row.resource
                                                                        .user
                                                                        .id;
                                                                const hasAccess =
                                                                    row.actions
                                                                        .edit;
                                                                return (
                                                                    <ProtectedLink
                                                                        to={`/users/${userId}`}
                                                                        hasAccess={
                                                                            hasAccess
                                                                        }
                                                                    >
                                                                        {name}
                                                                    </ProtectedLink>
                                                                );
                                                            },
                                                            layout: {
                                                                fixedWidth: 300,
                                                            },
                                                        }),
                                                    [USER_EXTRACTOR_KEYS.LOGIN_WITH_PASSWORD]:
                                                        () => ({
                                                            layout: {
                                                                fixedWidth: 150,
                                                            },
                                                        }),
                                                    [USER_EXTRACTOR_KEYS.EMAIL]:
                                                        () => ({
                                                            layout: {
                                                                fixedWidth: 300,
                                                            },
                                                        }),
                                                    [USER_EXTRACTOR_KEYS.PROVISION_STATUS]:
                                                        () => ({
                                                            display: scimEnabled
                                                                ? "DEFAULT_HIDDEN"
                                                                : "NEVER",
                                                            layout: {
                                                                fixedWidth: 150,
                                                            },
                                                        }),
                                                }}
                                            />
                                        </ContentWithoutBorder>
                                        {this.renderDialog(detailedUsers)}
                                    </UserModuleListApplicationBar>
                                );
                            }}
                        />
                    );
                }}
            />
        );
    };

    private onSendInvite = (
        _onSelectionActionDone: () => void,
        _formattedView: FormattedView<DetailedUserOverviewDtoContent>,
        selectedIds: Array<string>,
        _visibleColumnIds: Set<string>
    ) => {
        this.setState({
            dialog: {
                type: "SendInvite",
                users: selectedIds.map((x) => Number(x)),
                processing: false,
            },
        });
    };

    private enableSendInvite = (
        selectedIds: Array<string>,
        detailedUsers: DetailedUsersOverview
    ) => {
        if (selectedIds.length === 0) {
            return false;
        }

        const selection = selectedIds.map((id) => Number(id));
        const invitableUsers = detailedUsers.content
            .filter((user) => user.actions.invite)
            .map((user) => user.resource.user.id);
        return (
            selection.filter((s) => invitableUsers.includes(s)).length ===
            selection.length
        );
    };

    private onAssignRole = (
        _onSelectionActionDone: () => void,
        _formattedView: FormattedView<DetailedUserOverviewDtoContent>,
        selectedIds: Array<string>,
        _visibleColumnIds: Set<string>
    ) => {
        this.setState({
            dialog: {
                type: "AssignToRole",
                users: selectedIds.map((x) => Number(x)),
                processing: false,
            },
        });
    };

    private enableAssignRole = (
        formattedView: FormattedView<DetailedUserOverviewDtoContent>,
        selectedIds: Array<string>,
        _visibleColumnIds: Set<string>
    ) => {
        if (selectedIds.length === 0) {
            return false;
        }
        const selectedIdsSet = new Set<string>();
        selectedIds.forEach((id) => selectedIdsSet.add(id));
        return (
            DVFind(
                formattedView.dataView.dataSource.iter(),
                (duo) =>
                    selectedIdsSet.has(
                        formattedView.dataView.dataSource.getId(duo)
                    ) &&
                    duo.resource.assignableRoles.type === ALLOWED &&
                    duo.resource.assignableRoles.roles.length !== 0
            ) !== undefined
        );
    };

    private onExportUsers = (
        onSelectionActionDone: () => void,
        formattedView: FormattedView<DetailedUserOverviewDtoContent>,
        selectedIds: Array<string>,
        _visibleColumnIds: Set<string>
    ) => {
        // Generates the data to export
        const exportableArray = exportUserList(
            formattedView.dataView,
            selectedIds,
            this.props.controlTestingEnabled,
            this.props.controlEntitiesEnabled,
            this.props.mcpEnabled,
            this.props.scimEnabled
        );

        // Creates the XLSX file
        const metadata = newExportMetadata();
        const worksheet = XLSXUtils.tableToWorksheet(
            exportableArray,
            undefined,
            metadata
        );
        const workbook = XLSXUtils.workbookWithSingleWorksheet(
            "Users",
            worksheet
        );
        const data = XLSXUtils.workbookToBinary(workbook);

        // Downloads it
        const blob = new Blob([data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        saveAs(blob, formatFileNameForExport("Users", "xlsx"));

        onSelectionActionDone();
    };

    private addFilter =
        (
            oldFilter: SimpleAndFilter | null,
            onFilterChanged:
                | ((filter: SimpleAndFilter | null) => void)
                | undefined
        ) =>
        (extractorId: string, filter: SimpleFieldFilter, excluded: boolean) => {
            if (onFilterChanged) {
                onFilterChanged(
                    addFieldFilter(extractorId, filter, excluded, oldFilter)
                );
            }
        };

    private removeFilter =
        (
            oldFilter: SimpleAndFilter | null,
            onFilterChanged:
                | ((filter: SimpleAndFilter | null) => void)
                | undefined
        ) =>
        (extractorId: string, filter: SimpleFieldFilter, excluded: boolean) => {
            if (onFilterChanged) {
                onFilterChanged(
                    oldFilter &&
                        removeFieldFilter(
                            extractorId,
                            filter,
                            excluded,
                            oldFilter
                        )
                );
            }
        };

    private resetFilter =
        (
            oldFilter: SimpleAndFilter | null,
            onFilterChanged:
                | ((filter: SimpleAndFilter | null) => void)
                | undefined
        ) =>
        () => {
            if (onFilterChanged) {
                onFilterChanged(null);
            }
        };
}

function roleTag(role: Role, idx: number): React.ReactNode {
    const popoverContent = getLocalizedRoleDescription(role);
    return (
        <Col key={idx}>
            <Popover
                overlayClassName={theme.tagContent}
                content={popoverContent}
            >
                <Tag>{getLocalizedRoleName(role)}</Tag>
            </Popover>
        </Col>
    );
}

export {
    type LoginWithPassword,
    UserList,
    getLoginWithPassword,
    INVITE_PENDING,
    NOT_INVITED,
    ALLOWED,
    NOT_ALLOWED,
    shouldDisplayRoles,
};
